<template>
  <Loader :value="promise" theme="overlay">
    <section class="analytics-dashboard">
      <div class="analytics-dashboard__view container view">
        <div v-if="$slots.header" class="analytics-dashboard__header">
          <slot name="header" />
        </div>
        <div
          class="analytics-dashboard__body"
          :style="{
            gridTemplateAreas: gridTemplateArea,
            gridTemplateColumns: gridTemplateColumns,
          }"
        >
          <WidgetWrapper
            v-for="widget in widgets"
            :key="widget.key"
            :resource="widget.resource"
            :source="widget"
            :sources="sources"
            :id="item.id"
            :style="{ gridArea: widget.key }"
            :filter="filterComp"
          >
            <template #widget="{ data }">
              <component
                :is="widget.type"
                :source="widget"
                v-bind="options[widget]"
                :data="data"
                @linearFilter="updateFilter"
              />
            </template>
          </WidgetWrapper>
        </div>
      </div>
    </section>
  </Loader>
</template>
<script>
import moment from 'moment'
import 'moment/locale/nb'
import Widgets from '@/components/Analytics/Widgets'
import FilterComponent from '@/components/Analytics/Filter/Dropdown'
import WidgetWrapper from '@/components/Analytics/WidgetWrapper.vue'
import API from '@/api/Analytics'
import tracker from '@/tracking'

import {
  getWidgets,
  ValueTypes,
  defaultLinearFilters,
  isValidDate,
  createGrid,
  translationMap,
  getFormattedNumber,
} from '@/utils/analytics'

import {
  ActivityByLeads,
  BuyerHeading,
  DemographyHeading,
  DemographyBuyers,
  ResidentialStatus,
  Salesgrade,
  SalesPropertyType,
  ProjectStatus,
  ProjectHeading,
  ProjectPageStatus,
  TotalByPropertyType,
  TotalByStatus,
  LeadsHeading,
  StatusLeads,
  SourcesLeads,
  StatusPurchase,
  StatusSoldResidential,
} from './sources'

export default {
  components: {
    ...Widgets,
    FilterComponent,
    WidgetWrapper,
  },
  watch: {
    groupFilter: {
      handler: 'setupWidgets',
      deep: true,
      immediate: true,
    },
  },
  data() {
    return {
      promise: null,
      groupFilter: '',
      groups: [],
      language: this.$path('$store.state.enums.Language'),
      sources: [
        ResidentialStatus(this),
        Salesgrade(this),
        SalesPropertyType(this),
        ProjectStatus(this),
        TotalByPropertyType(this),
        TotalByStatus(this, 'sale'),
        TotalByStatus(this, 'reserved'),
        TotalByStatus(this, 'sold'),
        LeadsHeading(this),
        StatusLeads(this),
        SourcesLeads(this),
        ActivityByLeads(this),
        BuyerHeading(this),
        StatusPurchase(this),
        StatusSoldResidential(this),
        DemographyHeading(this),
        DemographyBuyers(this, 'gender', 'genderGroup'),
        DemographyBuyers(this, 'age', 'ageGroup'),
        DemographyBuyers(this, 'postal', 'postalGroup'),
        ProjectHeading(this),
        ProjectPageStatus(this),
      ],
      filters: null,
    }
  },
  props: {
    item: {
      type: Object,
      default: () => ({}),
    },
    type: {
      type: String,
      default: 'project',
    },
    options: {
      type: Object,
      default: () => ({}),
    },
    gridArea: {
      type: String,
      default: `project_data_validation project_data_validation project_data_validation | project_page_section:1fr project_page_section:1fr project_page_section:1fr | project_page_status:1fr project_page_status:1fr project_page_status:1fr | salesgrade:1fr project_status:1fr sales_property_type:1fr | status_sale_property:1fr status_sale_property:1fr status_sale_property:1fr | status_reserved_property:1fr status_reserved_property:1fr status_reserved_property:1fr | status_sold_property:1fr status_sold_property:1fr status_sold_property:1fr | leads_heading:1fr leads_heading:1fr leads_heading:1fr | status_leads:1fr status_leads:1fr status_leads:1fr | sources_leads:1fr activity_by_leads:1fr . | buyer_heading:1fr buyer_heading:1fr buyer_heading:1fr | status_purchase:1fr status_purchase:1fr status_purchase:1fr | status_sold_residential:1f status_sold_residential:1fr status_sold_residential:1fr | demography_heading demography_heading demography_heading | demography_gender_buyers:1fr demography_age_buyers:1fr demography_postal_buyers:1fr`,
    },
  },
  provide() {
    return {
      cache: this.cache,
    }
  },
  methods: {
    getTranslation(keys = [], separator = ' ') {
      keys = Array.isArray(keys) ? keys : [keys]
      if (keys.length === 1) return this.$tc(translationMap[keys[0]] || keys[0], 1)
      return keys.map(key => this.$tc(translationMap[key] || key, 1)).join(separator)
    },
    updateFilter({ source = {}, filter = {} }) {
      const sourceKey = source.key
      const filterKey = source.filterBy && source.filterBy.key ? source.filterBy.key : null
      const { value } = defaultLinearFilters.find(defaultFilter => defaultFilter.value === filter.value) || {}

      tracker.emit('Analytics:filter')

      if (!filterKey || !value) return this.filters
      this.filters[sourceKey] = {
        ...this.filters[sourceKey],
        filter: {
          ...this.filters[sourceKey].filter,
          [filterKey]: Number(value) ? moment().subtract(value, 'days').format('YYYY-MM-DD') : value,
        },
      }
    },
    valueParser(value, options = {}) {
      const {
        type = '',
        notation = 'standard',
        maximumSignificantDigits = 8,
        currency = '',
        dateFormat = 'DD. MMMM yy',
      } = options

      if (['undefined', undefined, null, false].includes(value)) return null
      if (type === ValueTypes.translate) return this.$t(value)
      if (type === ValueTypes.string) return this.$t(value)
      if (type === ValueTypes.propertyType) {
        return value || value === 0 ? `${value}` : this.$t('dataUnknown')
      }
      if (Number.isInteger(value) || [ValueTypes.currency, ValueTypes.number].includes(type))
        return getFormattedNumber(value, { notation, maximumSignificantDigits, language: this.language, currency })
      if (isValidDate(value) || type === ValueTypes.date) return moment(value).format(dateFormat)
      if (typeof value === 'string') return this.getTranslation(value)
      return value
    },
    setupWidgets() {
      this.promise = API.getDashboardWidget({
        id: this.item.id,
        preset: 'residential_groups',
      }).then(({ data }) => {
        this.groups = [
          { key: 'all', label: this.$t('all'), value: '' },
          ...data.map(group => ({ key: group, label: group, value: group })),
        ]
      })
    },
  },
  mounted() {
    this.filters = this.sources
      ? Object.fromEntries(
          this.sources
            .filter(source => typeof source.resource === 'object')
            .map(({ key, resource }) => [key, resource]),
        )
      : {}
  },
  computed: {
    filterComp() {
      return { ...this.filters, global: { 'filter.groups': this.groupFilter } }
    },
    cache() {
      return this.sources ? Object.fromEntries(this.sources.map(source => [[source.key], {}])) : {}
    },
    widgets() {
      return this.sources ? getWidgets({ widgets: Widgets, sources: this.sources, grid: this.gridArea }) : []
    },
    gridTemplateArea() {
      return createGrid(this.gridArea).templateArea
    },
    gridTemplateColumns() {
      return createGrid(this.gridArea).templateColumns
    },
  },
}
</script>

<style lang="scss">
.analytics-dashboard {
  background-color: $background-grey;

  &__view {
    display: flex;
    flex-direction: column;
    gap: 2rem;

    [class$='__chart'] {
      height: 100%;
      max-height: 30vh;
    }
  }
  &__body {
    min-height: 100vh;
    display: grid;
    grid-gap: 2rem;
    grid-template-rows: min-content;
    // grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)) !important;
    // grid-auto-columns: minmax(0, 1fr);
    // grid-auto-flow: column;
  }

  @media screen and (max-width: 1200px) {
    &__body {
      display: flex;
      flex-grow: 1;
      flex-wrap: wrap;
    }
  }

  @media screen and (max-width: 1000px) {
    &__body {
      display: grid;
      grid-gap: 2rem;
      grid-template-rows: min-content;
      grid-template-columns: 1fr !important;
      grid-template-areas: '.' !important;
    }
  }

  &__header {
    display: flex;
    justify-content: space-between;

    &__buttons {
      display: inline-flex;
    }
  }
}
</style>
